import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, User, MapPin, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const LeaderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [leader, setLeader] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeader = async () => {
      try {
        const response = await fetch(`/api/leaders/${id}`);
        if (response.ok) {
          setLeader(await response.json());
        } else {
          navigate('/leadership');
        }
      } catch (error) {
        console.error('Error fetching leader:', error);
        navigate('/leadership');
      } finally {
        setLoading(false);
      }
    };
    fetchLeader();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
      </div>
    );
  }

  if (!leader) return null;

  const hasImage = leader.image_url && !leader.image_url.includes('unsplash') && leader.image_url.length > 5;
  const isDistrict = leader.level === 'district';
  const isCentral = leader.level === 'central';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="leader-profile-page">
      {/* Hero Banner */}
      <section className="bg-party-red py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/leadership')}
            data-testid="back-to-leadership-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t({ en: 'Back to Leadership', bn: 'নেতৃত্বে ফিরে যান' })}
          </Button>
        </div>
      </section>

      {/* Profile Content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="md:flex">
            {/* Photo */}
            <div className="md:w-2/5 bg-gray-100">
              {hasImage ? (
                <img
                  src={leader.image_url}
                  alt={t(leader.name)}
                  className="w-full h-full object-cover object-top min-h-[400px] md:min-h-[500px]"
                  data-testid="leader-profile-image"
                />
              ) : (
                <div className="w-full h-full min-h-[400px] flex flex-col items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                  <User className="h-32 w-32 text-gray-400" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="md:w-3/5 p-8 md:p-10">
              <div className="mb-6">
                {isDistrict && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-party-green bg-party-green/10 px-3 py-1 rounded-full mb-3">
                    <MapPin className="h-3 w-3" />
                    {t({ en: 'District Leadership', bn: 'জেলা নেতৃত্ব' })}
                  </span>
                )}
                {isCentral && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full mb-3">
                    <Award className="h-3 w-3" />
                    {t({ en: 'Central Leadership', bn: 'কেন্দ্রীয় নেতৃত্ব' })}
                  </span>
                )}
                {!isDistrict && !isCentral && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-party-red bg-party-red/10 px-3 py-1 rounded-full mb-3">
                    <Award className="h-3 w-3" />
                    {t({ en: 'National Leadership', bn: 'জাতীয় নেতৃত্ব' })}
                  </span>
                )}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="leader-profile-name">
                  {t(leader.name)}
                </h1>
                <p className="text-xl text-party-red font-semibold" data-testid="leader-profile-position">
                  {t(leader.position)}
                </p>
              </div>

              <div className="w-16 h-1 bg-party-red rounded-full mb-6"></div>

              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed text-base" data-testid="leader-profile-bio">
                  {t(leader.bio)}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-400">
                  {t({ en: 'National Democratic Party of Bangladesh', bn: 'ন্যাশনাল ডেমোক্রেটিক পার্টি অফ বাংলাদেশ' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-16"></div>
    </div>
  );
};

export default LeaderProfile;
